/**
 * @fileoverview GitHub User Metrics Data Transformers
 * @description Transform per-user Copilot seat and usage data (from users-28-day report)
 *              into chart-ready structures. All data is per-user — no org-level metrics.
 */

/**
 * Get the best display label for a user.
 * Prefers display_name > seat.login > username.
 */
export function getUserLabel(u) {
  return u.display_name || u.seat?.login || u.username;
}

/**
 * Get Copilot seat status summary — for a pie chart
 * (active, inactive, no seat)
 */
export function getSeatStatusBreakdown(users) {
  const activeUsers = users.filter((u) => u.still_in_account !== false);
  let active = 0;
  let inactive = 0;
  let noSeat = 0;

  for (const u of activeUsers) {
    if (!u.has_copilot_seat) {
      noSeat++;
    } else if (u.seat?.days_since_active != null && u.seat.days_since_active <= 10) {
      active++;
    } else {
      inactive++;
    }
  }

  return [
    { id: 'active', label: 'Active (≤10d)', value: active },
    { id: 'inactive', label: 'Inactive (>10d)', value: inactive },
    { id: 'no_seat', label: 'No Copilot Seat', value: noSeat },
  ].filter((d) => d.value > 0);
}

/**
 * Inactive Copilot users — those with activity older than 10 days
 * or users with a seat but no recorded activity.
 * Returns all inactive users (no limit).
 */
export function getLeastActiveCopilotUsers(users) {
  const activeUsers = users.filter((u) => u.still_in_account !== false);
  const withActivity = activeUsers
    .filter((u) => u.has_copilot_seat && u.seat?.days_since_active != null && u.seat.days_since_active > 10)
    .map((u) => ({
      username: getUserLabel(u),
      githubUsername: u.username,
      days: u.seat.days_since_active,
      editor: formatEditorName(u.seat?.last_activity_editor),
    }))
    .sort((a, b) => b.days - a.days);

  const noActivity = activeUsers
    .filter((u) => u.has_copilot_seat && u.seat?.days_since_active == null)
    .map((u) => ({
      username: getUserLabel(u),
      githubUsername: u.username,
      days: -1,
      editor: 'None',
    }));

  return [...noActivity, ...withActivity];
}

/**
 * Per-user LOC added (28-day total) — for a horizontal bar chart
 * Shows which users have added the most lines of code via Copilot.
 */
export function getLocAddedByUser(users, topN = 15) {
  return users
    .filter((u) => u.usage?.loc_added > 0)
    .map((u) => ({
      username: getUserLabel(u),
      githubUsername: u.username,
      locAdded: u.usage.loc_added,
      locSuggested: u.usage.loc_suggested || 0,
    }))
    .sort((a, b) => b.locAdded - a.locAdded)
    .slice(0, topN);
}

/**
 * Per-user acceptance rate (28-day) — for a horizontal bar chart
 * Shows how effectively each user accepts Copilot suggestions.
 */
export function getAcceptanceRateByUser(users, topN = 15) {
  return users
    .filter((u) => u.usage?.completions_generated > 0)
    .map((u) => ({
      username: getUserLabel(u),
      githubUsername: u.username,
      rate: u.usage.acceptance_rate,
      accepted: u.usage.completions_accepted,
      generated: u.usage.completions_generated,
    }))
    .sort((a, b) => b.rate - a.rate)
    .slice(0, topN);
}

/**
 * Per-user active days (28-day) — for a horizontal bar chart
 * Shows how many out of 28 days each user was active.
 */
export function getActiveDaysByUser(users) {
  return users
    .filter((u) => u.usage?.active_days > 0)
    .map((u) => ({
      username: getUserLabel(u),
      githubUsername: u.username,
      activeDays: u.usage.active_days,
      totalDays: u.usage.total_days || 28,
    }))
    .sort((a, b) => b.activeDays - a.activeDays);
}

/**
 * Per-user chat interactions (28-day) — for a bar chart
 */
export function getChatInteractionsByUser(users, topN = 15) {
  return users
    .filter((u) => u.usage?.chat_interactions > 0)
    .map((u) => ({
      username: getUserLabel(u),
      githubUsername: u.username,
      interactions: u.usage.chat_interactions,
    }))
    .sort((a, b) => b.interactions - a.interactions)
    .slice(0, topN);
}

/**
 * Per-user IDE usage — aggregate IDE usage across all tracked users
 */
export function getIdeUsageAcrossUsers(users) {
  const ideCounts = {};
  for (const u of users) {
    const ides = u.usage?.ide_usage ?? {};
    for (const [ide, count] of Object.entries(ides)) {
      const name = formatEditorName(ide);
      ideCounts[name] = (ideCounts[name] || 0) + count;
    }
  }
  return Object.entries(ideCounts)
    .map(([editor, activity]) => ({ editor, activity }))
    .sort((a, b) => b.activity - a.activity);
}

/**
 * Per-user language usage — aggregate language usage across all tracked users
 */
export function getLanguageUsageAcrossUsers(users) {
  const langCounts = {};
  for (const u of users) {
    const langs = u.usage?.language_usage ?? {};
    for (const [lang, count] of Object.entries(langs)) {
      langCounts[lang] = (langCounts[lang] || 0) + count;
    }
  }
  return Object.entries(langCounts)
    .map(([label, value]) => ({ id: label, label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 15);
}

/**
 * Feature usage breakdown — code_completion, chat, agent_edit, etc.
 */
export function getFeatureUsageAcrossUsers(users) {
  const featureCounts = {};
  for (const u of users) {
    const feats = u.usage?.feature_usage ?? {};
    for (const [feat, count] of Object.entries(feats)) {
      const name = formatFeatureName(feat);
      featureCounts[name] = (featureCounts[name] || 0) + count;
    }
  }
  return Object.entries(featureCounts)
    .map(([id, value]) => ({ id, label: id, value }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Get per-user last editor used — count of users per editor
 */
export function getLastEditorByUser(users) {
  const activeUsers = users.filter((u) => u.still_in_account !== false);
  const editorCounts = {};
  for (const u of activeUsers) {
    if (u.has_copilot_seat && u.seat?.last_activity_editor) {
      const editor = formatEditorName(u.seat.last_activity_editor);
      editorCounts[editor] = (editorCounts[editor] || 0) + 1;
    }
  }
  return Object.entries(editorCounts)
    .map(([editor, count]) => ({ editor, count }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Get summary statistics
 */
export function getSummaryStats(users) {
  let withSeat = 0;
  let activeRecent = 0;
  let totalLocAdded = 0;
  let totalAccepted = 0;
  let totalChatInteractions = 0;

  for (const u of users) {
    if (u.has_copilot_seat) withSeat++;
    if (u.seat?.days_since_active != null && u.seat.days_since_active <= 10) activeRecent++;
    totalLocAdded += u.usage?.loc_added ?? 0;
    totalAccepted += u.usage?.completions_accepted ?? 0;
    totalChatInteractions += u.usage?.chat_interactions ?? 0;
  }

  return {
    totalUsers: users.length,
    withSeat,
    activeRecent,
    totalLocAdded,
    totalAccepted,
    totalChatInteractions,
  };
}

/**
 * Format editor name for display (strip version numbers)
 */
function formatEditorName(name) {
  if (!name) return 'Unknown';
  const lower = name.toLowerCase();
  if (lower.includes('vscode') || lower.includes('visual studio code')) return 'VS Code';
  if (lower.includes('jetbrains') || lower.includes('intellij')) return 'JetBrains';
  if (lower.includes('neovim')) return 'Neovim';
  if (lower.includes('vim')) return 'Vim';
  if (lower.includes('xcode')) return 'Xcode';
  if (lower.includes('eclipse')) return 'Eclipse';
  return name.split('/')[0] || name;
}

/**
 * Format feature name for display
 */
function formatFeatureName(name) {
  if (!name) return 'Unknown';
  const map = {
    code_completion: 'Code Completion',
    inline_chat: 'Inline Chat',
    chat_panel: 'Chat Panel',
    agent_edit: 'Agent/Edit',
    code_review: 'Code Review',
  };
  return map[name] || name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format date for chart labels
 */
function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

// ── Token Usage & Cost Transformers ──────────────────────────

/**
 * Get premium requests per user — for a horizontal bar chart
 */
export function getPremiumRequestsByUser(users, topN = 15) {
  return users
    .filter((u) => u.token_usage?.premium_requests?.total > 0)
    .map((u) => ({
      username: getUserLabel(u),
      githubUsername: u.username,
      premiumRequests: u.token_usage.premium_requests.total,
      estimatedCost: u.token_usage.estimated_cost_usd || 0,
    }))
    .sort((a, b) => b.premiumRequests - a.premiumRequests)
    .slice(0, topN);
}

/**
 * Get estimated cost per user — for a horizontal bar chart
 */
export function getCostByUser(users, topN = 15) {
  return users
    .filter((u) => u.token_usage?.estimated_cost_usd > 0)
    .map((u) => ({
      username: getUserLabel(u),
      githubUsername: u.username,
      cost: u.token_usage.estimated_cost_usd,
    }))
    .sort((a, b) => b.cost - a.cost)
    .slice(0, topN);
}

/**
 * Get model usage across all users — for a pie chart
 */
export function getModelUsageAcrossUsers(users) {
  const modelCounts = {};
  for (const u of users) {
    const byModel = u.token_usage?.premium_requests?.by_model ?? {};
    for (const [model, count] of Object.entries(byModel)) {
      if (count > 0) {
        modelCounts[model] = (modelCounts[model] || 0) + count;
      }
    }
  }
  return Object.entries(modelCounts)
    .map(([id, value]) => ({ id, label: formatModelName(id), value }))
    .sort((a, b) => b.value - a.value);
}

/**
 * Get CLI token usage per user — for users who use Copilot CLI
 */
export function getCliTokensByUser(users, topN = 15) {
  return users
    .filter((u) => u.token_usage?.cli?.total_tokens > 0)
    .map((u) => ({
      username: getUserLabel(u),
      githubUsername: u.username,
      totalTokens: u.token_usage.cli.total_tokens,
      promptTokens: u.token_usage.cli.prompt_tokens,
      outputTokens: u.token_usage.cli.output_tokens,
    }))
    .sort((a, b) => b.totalTokens - a.totalTokens)
    .slice(0, topN);
}

/**
 * Get token usage summary statistics
 */
export function getTokenUsageSummary(users) {
  let totalPremiumRequests = 0;
  let totalEstimatedCost = 0;
  let totalCliTokens = 0;
  let usersWithTokenData = 0;

  for (const u of users) {
    const tu = u.token_usage;
    if (!tu) continue;
    if (tu.premium_requests?.total > 0 || tu.cli?.total_tokens > 0) {
      usersWithTokenData++;
    }
    totalPremiumRequests += tu.premium_requests?.total ?? 0;
    totalEstimatedCost += tu.estimated_cost_usd ?? 0;
    totalCliTokens += tu.cli?.total_tokens ?? 0;
  }

  return {
    totalPremiumRequests,
    totalEstimatedCost: Math.round(totalEstimatedCost * 100) / 100,
    totalCliTokens,
    usersWithTokenData,
  };
}

/**
 * Format model name for display
 */
export function formatModelName(name) {
  if (!name) return 'Unknown';
  if (name === 'copilot_cli') return 'Copilot CLI';
  if (name === 'auto') return 'Auto';
  if (name === 'others') return 'Others';
  if (name === 'unknown') return 'Unknown';
  return name;
}
